import React, { useState } from 'react'
import { X, Clock, User, ShoppingCart, Trash2, Play, Package } from 'lucide-react'
import { HeldTransaction, CartItem } from '@/types/pos'

interface HoldTransactionsModalProps {
  heldTransactions: HeldTransaction[]
  onResume: (transaction: HeldTransaction) => void
  onDelete: (transactionId: string) => void
  onClose: () => void
}

export const HoldTransactionsModal: React.FC<HoldTransactionsModalProps> = ({
  heldTransactions,
  onResume,
  onDelete,
  onClose,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  const formatTimeAgo = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - new Date(date).getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(minutes / 60)
    
    if (hours > 0) return `${hours}h ${minutes % 60}m ago`
    if (minutes > 0) return `${minutes}m ago`
    return 'Just now'
  }

  const calculateTotal = (items: CartItem[]) => {
    return items.reduce((sum, item) => sum + (item.product.price * item.quantity), 0)
  }

  const handleDelete = (id: string) => {
    if (confirmDelete === id) {
      onDelete(id)
      setConfirmDelete(null)
    } else {
      setConfirmDelete(id)
      setTimeout(() => setConfirmDelete(null), 3000)
    }
  }

  const selectedTransaction = heldTransactions.find(t => t.id === selectedId)

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold esales-text flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Held Transactions
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {heldTransactions.length} transaction{heldTransactions.length !== 1 ? 's' : ''} on hold
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {heldTransactions.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <div className="w-20 h-20 bg-muted/30 rounded-full flex items-center justify-center mb-4">
              <Package className="w-10 h-10 text-muted-foreground" />
            </div>
            <h4 className="font-semibold text-lg mb-2">No Held Transactions</h4>
            <p className="text-muted-foreground text-sm max-w-xs">
              When you hold a transaction, it will appear here so you can resume it later.
            </p>
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-y-auto">
            {/* Transactions List */}
            <div className="space-y-3">
              {heldTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  onClick={() => setSelectedId(transaction.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    selectedId === transaction.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border/50 hover:border-primary/30 bg-muted/10'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-bold text-sm">{transaction.name}</h4>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        {formatTimeAgo(transaction.heldAt)}
                      </p>
                    </div>
                    <span className="text-lg font-bold text-primary">
                      Rs.{calculateTotal(transaction.cart).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <ShoppingCart className="w-3 h-3" />
                      {transaction.cart.length} item{transaction.cart.length !== 1 ? 's' : ''}
                    </span>
                    {transaction.customerName && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {transaction.customerName}
                      </span>
                    )}
                  </div>

                  {selectedId === transaction.id && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-border/50">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onResume(transaction)
                        }}
                        className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-primary/90 transition-colors"
                      >
                        <Play className="w-3 h-3" />
                        Resume
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(transaction.id)
                        }}
                        className={`px-4 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${
                          confirmDelete === transaction.id
                            ? 'bg-red-500 text-white'
                            : 'bg-red-500/10 text-red-500 hover:bg-red-500/20'
                        }`}
                      >
                        <Trash2 className="w-3 h-3" />
                        {confirmDelete === transaction.id ? 'Confirm' : 'Delete'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Selected Transaction Preview */}
            {selectedTransaction && (
              <div className="glass-card bg-muted/20 p-4 rounded-xl h-fit sticky top-0">
                <h4 className="font-bold mb-3 text-sm">Cart Preview</h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {selectedTransaction.cart.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-background/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-10 flex-shrink-0 flex items-center justify-center">
                          {(item.product.image?.startsWith('http') || item.product.image?.startsWith('data:')) ? (
                            <img 
                              src={item.product.image} 
                              alt={item.product.name}
                              className="w-full h-full object-cover rounded"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none'
                              }}
                            />
                          ) : (
                            <span className="text-lg">{item.product.image || '📖'}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-semibold truncate max-w-[150px]">{item.product.name}</p>
                          <p className="text-[10px] text-muted-foreground">Rs.{item.product.price.toFixed(2)} × {item.quantity}</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-primary">
                        Rs.{(item.product.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {selectedTransaction.notes && (
                  <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">
                      <strong>Note:</strong> {selectedTransaction.notes}
                    </p>
                  </div>
                )}

                <div className="mt-4 pt-3 border-t border-border/50">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold">Total</span>
                    <span className="text-xl font-bold text-primary">
                      Rs.{calculateTotal(selectedTransaction.cart).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex-shrink-0 mt-4 pt-4 border-t border-border/50">
          <button
            onClick={onClose}
            className="w-full py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default HoldTransactionsModal
